// Surveys (DESIGN §2.3) and leases (§2.4).
import { wellsOnField } from './indexes.js';
import { makeTaken, makeRivalWell } from './shapes.js';
import { normal, nextFloat, chance, int, range, pick } from '../core/rng.js';
import { reservoirsAt } from './geo.js';
import { interpCurve } from './finance.js';
import { eqDef, fieldDef, regionDef, stat, isAvailable, unitPrice, eraDollars, yf, yearOf } from './defs.js';
import { startTimer } from './timers.js';
import { clamp, notify, log, spend, earn, fmt$ } from './util.js';
import { surveyPriceMult, surveySigma, geologistSigmaDelta, applyDepthEstimates } from './m2/geophysics.js';
import { regionBlock } from './m2/politics.js';
import { allowableFn } from './m2/proration.js';
import { patentFeeFor, chargePatentFee } from './m2/licensing.js';
import { scheduledRoyalty } from '../data/m2/regions.js';
import { generateOperatorName } from '../data/operators.js';
import { TIER_LEASE_MULT, plotTier } from '../data/m4/fields.js'; // M4 balance: shale acreage priced by tier
import { m3LeaseGate, m3AfterLease } from './m3/leaserounds.js'; // M3 econ: rounds-only acreage, concession bonus, JV partner

export const LEASE_TERM_DAYS = 3 * 365;

// ---- surveys ----------------------------------------------------------------
export function surveyImpl(state, fieldId, method) {
  const f = state.fields[fieldId];
  const fdef = fieldDef(fieldId);
  const def = eqDef(method);
  if (!f || !fdef) return { ok: false, msg: 'No such field.' };
  if (!fieldOpen(state, fieldId)) return { ok: false, msg: `${fdef.name} is not open yet.` };
  if (!def || def.cat !== 'survey') return { ok: false, msg: 'Unknown survey method.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} isn't known until ${def.year}.` };
  if (stat(def, 'perWell', false)) return { ok: false, msg: `${def.name} is run on a well, from the well panel.` };
  const blocked = regionBlock(state, fieldId);
  if (blocked) return { ok: false, msg: blocked };
  if (Object.values(state.timers).some((t) => t.kind === 'SURVEY' && t.refId === fieldId && t.data.method === method)) return { ok: false, msg: 'That survey is already under way.' };
  f.surveysDone ??= [];
  if (f.surveysDone.includes(method) && !stat(def, 'random', false)) return { ok: false, msg: `${def.name} already done here — try a better method.` };
  const role = stat(def, 'needsRole', null);
  let surveyor = null;
  if (role) {
    surveyor = Object.values(state.crew).find((c) => c.role === role && c.status === 'active' && !c.injuredUntil && !c.rigId);
    if (!surveyor) return { ok: false, msg: `Hire a ${role} first.` };
  }
  const price = unitPrice(state, def) * surveyPriceMult(state, def); // M2: in-house geophysicist halves seismic/gravity
  const fee = patentFeeFor(state, def.id, 1);                           // M2: Seismos' refraction patent (1919-36)
  if (price + (fee?.fee ?? 0) > 0 && state.company.cash < price + (fee?.fee ?? 0) - 1e-9) return { ok: false, msg: `Need ${fmt$(price + (fee?.fee ?? 0))}${fee ? ` (incl. ${fmt$(fee.fee)} licence fee to ${fee.holder})` : ''}.` };
  if (price > 0) spend(state, price, 'other');
  if (fee) chargePatentFee(state, fee);
  const t = startTimer(state, 'SURVEY', fieldId, stat(def, 'days', 1), { method, crewId: surveyor?.id ?? null });
  return { ok: true, msg: `${def.name} survey of ${fdef.name} under way (${t.daysLeft} day${t.daysLeft > 1 ? 's' : ''}).` };
}

export function onSurveyDone(state, t) {
  const f = state.fields[t.refId];
  const def = eqDef(t.data.method);
  if (!f || !def) return;
  const random = stat(def, 'random', false);
  const fdef = fieldDef(f.id);
  let sigma = surveySigma(state, def, fdef);            // M2: torsion balance / seismic read salt domes sharper
  let seepSigma = surveySigma(state, def, fdef, true);
  const surveyor = t.data.crewId ? state.crew[t.data.crewId] : null;
  if (surveyor?.traits?.includes('lucky')) { sigma -= 0.05; seepSigma -= 0.05; }
  // anticline theory (−0.05) and micropaleontology (−0.04) via research effects (was a dead check on 'anticlinal_theory')
  if (def.id === 'geologist') { const d = geologistSigmaDelta(state); sigma += d; seepSigma += d; }
  for (const p of f.plots) {
    const truth = p.truth.prospectIdx >= 0 ? 1 : 0;
    const sg = Math.max(0.05, p.seep ? seepSigma : sigma);
    const est = random ? nextFloat(state) : clamp(truth + normal(state, 0, sg), 0, 1);
    p.survey = combine(p.survey, est, sg, def.id);
  }
  applyDepthEstimates(state, f.id, def); // M2: seismic depthErr → estimated pay depth per plot
  f.surveysDone ??= [];
  if (!f.surveysDone.includes(def.id)) f.surveysDone.push(def.id);
  if (random) state.company.stats.dowsed = (state.company.stats.dowsed ?? 0) + 1;
  notify(state, 'news', { fieldId: f.id, kind: 'survey', text: `${def.name} survey of ${fieldDef(f.id)?.name} complete.` });
}

// Inverse-variance stacking of survey estimates.
export function combine(prev, est, sigma, method) {
  if (!prev) return { est: round3(est), sigma: round3(sigma), methods: [method] };
  const w1 = 1 / (prev.sigma * prev.sigma), w2 = 1 / (sigma * sigma);
  return {
    est: round3((prev.est * w1 + est * w2) / (w1 + w2)),
    sigma: round3(1 / Math.sqrt(w1 + w2)),
    methods: [...prev.methods, method],
  };
}
const round3 = (x) => Math.round(x * 1000) / 1000;

// ---- leases -----------------------------------------------------------------
// Pure in (field, day): memoized per field on the last day asked (hot: every field, every tick, plus UI).
const OPEN_MEMO = new Map();
export function fieldOpen(state, fieldId) {
  const m = OPEN_MEMO.get(fieldId);
  if (m && m.day === state.day) return m.v;
  const v = fieldOpenRaw(state, fieldId);
  OPEN_MEMO.set(fieldId, { day: state.day, v });
  return v;
}
function fieldOpenRaw(state, fieldId) {
  const fdef = fieldDef(fieldId);
  if (!fdef) return false;
  if (fdef.opensDay != null) return state.day >= fdef.opensDay;
  if (fdef.opensYear != null) return yf(state) >= fdef.opensYear;
  const rdef = regionDef(fdef.regionId);
  return rdef?.opensYear ? yf(state) >= rdef.opensYear : true;
}

const BOOM_MEMO = new Map(); // fdef -> { day, v }
function boomCurveAt(fdef, day, y) {
  const m = BOOM_MEMO.get(fdef);
  if (m && m.day === day) return m.v;
  const v = interpCurve(fdef.boomCurve, y);
  BOOM_MEMO.set(fdef, { day, v });
  return v;
}
export function boomFactor(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const f = state.fields[fieldId];
  let b = fdef?.boomCurve ? boomCurveAt(fdef, state.day, yf(state)) : 1;
  if (f?.boomEvent) b *= f.boomEvent;
  return b;
}

export function repMult(state) {
  // rep 0 → ×1.2, rep 50 → ×1.0, rep 100 → ×0.8
  return 1.2 - 0.4 * clamp(state.company.reputation, 0, 100) / 100;
}

// Lease fever: each strike in a field (yours or a rival's) makes landowners greedier.
export function fieldStrikes(state, fieldId) {
  const f = state.fields[fieldId];
  let n = 0;
  for (const p of f.plots) if (p.taken?.well && !p.taken.well.dry) n++;
  for (const w of wellsOnField(state, fieldId)) if (w.reservoirId) n++;
  return n;
}

export function leaseRoyalty(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const sched = scheduledRoyalty(fdef.regionId, yf(state)); // M2: concession royalty schedules (Venezuela 1/6 from 1943...)
  if (sched != null) return sched;
  const base = fdef.royalty ?? regionDef(fdef.regionId)?.royalty ?? 0.125;
  return Math.round(Math.min(0.25, base + 0.01 * fieldStrikes(state, fieldId)) * 1000) / 1000;
}

export function leasePrice(state, fieldId, plotIdx) {
  const fdef = fieldDef(fieldId);
  const p = state.fields[fieldId].plots[plotIdx];
  const rdef = regionDef(fdef.regionId);
  const heat = 1 + 2 * (p.pubEst ?? 0.3);
  const base = eraDollars(state, fdef.leaseBase ?? 300, fdef.leaseYear ?? 1859);
  const fever = Math.min(4, 1 + 0.12 * fieldStrikes(state, fieldId));
  // M4 balance: shale DSUs are priced by acreage tier (core ×2.5, fairway ×1, fringe ×0.25 — fields.js TIER_LEASE_MULT)
  const tier = fdef.shale && yf(state) >= 2000 ? TIER_LEASE_MULT[reservoirsAt(fdef, p).length ? plotTier(fdef, plotIdx) : 3] ?? 1 : 1;
  return Math.round(base * heat * fever * (p.priceMult ?? 1) * boomFactor(state, fieldId) * repMult(state) * (fdef.leaseMult ?? rdef?.leaseMult ?? 1) * tier);
}

export function leaseImpl(state, fieldId, plotIdx) {
  const f = state.fields[fieldId];
  if (!f) return { ok: false, msg: 'No such field.' };
  if (!fieldOpen(state, fieldId)) return { ok: false, msg: `${fieldDef(fieldId).name} isn't open yet.` };
  const p = f.plots[plotIdx];
  if (!p) return { ok: false, msg: 'No such plot.' };
  const blocked = regionBlock(state, fieldId); // M2: closed / occupied regions
  if (blocked) return { ok: false, msg: blocked };
  if (p.owned) return { ok: false, msg: 'You already hold that lease.' };
  if (p.wellId) return { ok: false, msg: 'That plot is taken.' };
  if (p.taken) return { ok: false, msg: `${p.taken.by} holds that lease.` };
  const price = leasePrice(state, fieldId, plotIdx);
  if (state.company.cash < price) return { ok: false, msg: `Lease costs ${fmt$(price)}; not enough cash.` };
  const m3 = m3LeaseGate(state, fieldId, price); // M3 econ (signs a concession when the first lease needs it)
  if (m3) return { ok: false, msg: m3 };
  if (!spend(state, price, 'leases')) return { ok: false, msg: `Lease costs ${fmt$(price)}; not enough cash.` };
  p.owned = true;
  p.leaseDay = state.day;
  p.leaseExpires = state.day + LEASE_TERM_DAYS;
  p.leasePaid = price;
  p.royalty = leaseRoyalty(state, fieldId);
  m3AfterLease(state, fieldId); // M3 econ
  return { ok: true, msg: `Leased plot ${plotIdx + 1} on ${fieldDef(fieldId).name} for ${fmt$(price)}. Spud within 3 years.` };
}

export function sellLeaseImpl(state, fieldId, plotIdx) {
  const p = state.fields[fieldId]?.plots[plotIdx];
  if (!p?.owned) return { ok: false, msg: "You don't hold that lease." };
  if (p.wellId) return { ok: false, msg: 'Only undrilled leases can be sold.' };
  const value = Math.round(leasePrice(state, fieldId, plotIdx) * 0.7);
  earn(state, value, 'other');
  p.owned = false; p.leaseDay = null; p.leaseExpires = null; p.royalty = null;
  notify(state, 'leaseSold', { fieldId, plotIdx, paid: p.leasePaid ?? 0, value }); // newspaper: Pithole lease flips (§18)
  return { ok: true, msg: `Lease sold back for ${fmt$(value)}.` };
}

const LEASE_WARN_DAYS = 60;
function expireLease(state, f, p, i) {
  if (p.owned && !p.wellId && p.leaseExpires != null && p.leaseExpires - state.day === LEASE_WARN_DAYS)
    notify(state, 'news', { fieldId: f.id, kind: 'lease', text: `Your lease on plot ${i + 1} at ${fieldDef(f.id)?.name} runs out in ${LEASE_WARN_DAYS} days unless you spud a well on it.`, level: 'warn' });
  if (p.owned && !p.wellId && p.leaseExpires != null && state.day >= p.leaseExpires) {
    p.owned = false; p.leaseExpires = null; p.leaseDay = null;
    state.company.reputation = Math.max(0, state.company.reputation - 1);
    notify(state, 'news', { fieldId: f.id, kind: 'lease', text: `Your lease on plot ${i + 1} at ${fieldDef(f.id)?.name} expired undrilled.`, level: 'warn' });
  }
}

// One pass over each field's plots per day: player lease expiry, rival drilling / abandonment / production, and the
// monthly decay of neighbour price bumps. Rival *leasing* is sampled per field (see tickRivals), not per plot.
export function tickLeases(state) {
  const monthly = state.day % 30 === 0;
  for (const f of Object.values(state.fields)) {
    f.boomFactor = Math.round(boomFactor(state, f.id) * 100) / 100;
    // open fields: lease expiry is checked inside tickRivals' single pass
    if (!tickRivals(state, f)) for (let i = 0; i < f.plots.length; i++) expireLease(state, f, f.plots[i], i);
    // neighbour strike bumps decay slowly back toward 1
    if (monthly) for (const p of f.plots) if (p.priceMult > 1) p.priceMult = Math.max(1, p.priceMult * 0.98);
  }
}

// ---- rival wildcatters (rule of capture) -----------------------------------------------
// Other operators lease free plots over time and drill them; their wells drain shared reservoirs.
export const RIVAL_RATE = 0.0003;
const RIVALS = ['Brewer, Watson & Co.', 'Clapp Bros.', 'Funk & Co.', 'Phillips Bros.', 'Rouse & Mitchell', 'Columbia Oil', 'Densmore & Co.', 'Pittsburgh Petroleum', 'Tarr Farm Co.', 'Noble & Delamater'];

// polish (C4): rival names fit the era and region. One RNG draw, as before (pick() used one): the 1859–1900
// Pennsylvania-style list is unchanged; later / elsewhere a name is derived from that same draw.
function rivalName(state, fdef, u) {
  const y = yearOf(state), reg = fdef?.regionId ?? 'pennsylvania';
  if (y < 1901 && (reg === 'pennsylvania' || reg === 'ohio')) return RIVALS[Math.floor(u * RIVALS.length)];
  let x = Math.floor(u * 2147483646) + 1; // tiny LCG seeded by the draw (not the game RNG)
  const rand = () => (x = (x * 48271) % 2147483647) / 2147483647;
  return generateOperatorName(rand, { year: y, regionId: reg }).name;
}

// Rival leasing is a per-plot hazard p_i = RIVAL_RATE × (1 + 2·pubEst_i) × boom on every free plot. It used to be one
// Bernoulli draw per free plot per day (~1,100 draws a day by the 1970s). Now it is sampled per field: the number of
// new rival leases today is Poisson(Σ p_i) (the same mean; the per-plot form only differed by O(p²)), each lands on a
// free plot chosen ∝ p_i, so a quiet field costs one draw a day. Rival drilling and production are unchanged.
// Returns false when the field is not open (nothing done); true after the pass (which also expired player leases).
export function tickRivals(state, f) {
  if (!fieldOpen(state, f.id)) return false;
  const fdef = fieldDef(f.id);
  const boom = Math.min(4, f.boomFactor || 1);
  const plots = f.plots;
  let allow = null; // today's allowable rule for this field, resolved on first use
  let w = 0; // Σ (1 + 2·pubEst) over free plots
  for (let i = 0; i < plots.length; i++) {
    const p = plots[i];
    if (p.owned) { expireLease(state, f, p, i); if (p.owned) continue; }
    if (p.wellId) continue;
    const t = p.taken;
    if (!t) { w += 1 + 2 * (p.pubEst ?? 0.3); continue; }
    if (!t.well && state.day >= t.drillDay) {
      const r = reservoirsAt(fdef, p)[0];
      if (r) {
        const [lo, hi] = r.qiRange ?? [5, 30];
        t.well = makeRivalWell({ resId: r.id, qi: Math.round((lo + (hi - lo) * nextFloat(state)) * 0.5 * 10) / 10, startDay: state.day, di: r.di ?? 0.6, b: r.b ?? 0.5 });
        p.pubEst = 0.95;
        // a strike next door makes the neighbours dearer
        for (const q of plots) if (!q.owned && Math.max(Math.abs(q.x - p.x), Math.abs(q.y - p.y)) === 1) q.priceMult = Math.min(8, q.priceMult * 1.2);
        if (chance(state, 0.3)) notify(state, 'news', { fieldId: f.id, kind: 'rival', text: `${t.by} struck oil on plot ${i + 1} at ${fdef.name}.` });
      } else {
        t.well = makeRivalWell({ dry: true });
        p.pubEst = 0.05;
      }
    }
    const well = t.well;
    if (!well) continue;
    // dry rival holes are abandoned and the lease lapses (cheap, but now known dry)
    if (well.dry) { if (state.day - t.drillDay > 180) { p.taken = null; w += 1 + 2 * (p.pubEst ?? 0.3); } continue; }
    // rival production drains the shared pool
    const res = f.reservoirs[well.resId];
    if (!res || res.remaining <= 0) continue;
    const yrs = (state.day - well.startDay) / 365;
    const x = 1 + well.b * well.di * yrs;
    let q = well.qi / (well.b === 0.5 ? x * x : Math.pow(x, 1 / well.b)) * Math.sqrt(res.remaining / res.initial) * (res.pressure < 0.55 ? 0.3 : 1); // b = 0.5 (most pools): x^2
    if (fdef.prorationGroup) q = Math.min(q, (allow ??= allowableFn(state, f.id))(q)); // M2: rivals are prorated too
    res.remaining = Math.max(0, res.remaining - q);
  }
  // today's new rival leases: Poisson(λ) by sequential thinning, λ = RIVAL_RATE × boom × w
  const k = RIVAL_RATE * boom;
  while (w > 1e-9 && nextFloat(state) < -Math.expm1(-k * w)) {
    let x = nextFloat(state) * w, pick = -1;
    for (let i = 0; i < plots.length; i++) {
      const p = plots[i];
      if (p.owned || p.wellId || p.taken) continue;
      pick = i;
      x -= 1 + 2 * (p.pubEst ?? 0.3);
      if (x < 0) break;
    }
    if (pick < 0) break;
    const p = plots[pick];
    w -= 1 + 2 * (p.pubEst ?? 0.3);
    p.taken = makeTaken({ by: rivalName(state, fdef, nextFloat(state)), day: state.day, drillDay: state.day + int(state, 20, 90), well: null });
  }
  return true;
}
